import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild, NgZone } from '@angular/core';
import { PaymentSheetOption } from '../components/payment-method-sheet/payment-method-sheet.component';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { ApiService, CategoryResponse, ProductResponse, FullMenuResponse, CategoryWithProducts as ApiCategoryWithProducts } from '../services/api.service';
import { CartService, CartItem } from '../services/cart.service';
import { BannerService, BannerPromotion } from '../services/banner.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { fadeInOut, slideInOut, listAnimation, scaleInOut, bounceInOut } from '../shared/animations';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { NotificationService } from '../services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LanguageService, Language } from '../services/language.service';
import { AnalyticsService, AnalyticsEntry } from '../services/analytics.service';
import { BranchService } from '../services/branch.service';
import { CurrencyService } from '../services/currency.service';
import { ShopStatusService } from '../services/shop-status.service';
import { GuestPushService, PushPromptSource } from '../services/guest-push.service';
import { ProductSheetService } from '../services/product-sheet.service';
import { LanguageSheetService } from '../services/language-sheet.service';
import { AttractionsEntryService } from '../services/attractions-entry.service';
import { ReelService } from '../services/reel.service';
import { ReelPreloadService } from '../services/reel-preload.service';
import { NotificationDeepLinkService } from '../services/notification-deep-link.service';
import { OrderHistoryService } from '../services/order-history.service';
import { shouldShowOrderBar } from '../shared/order-bar-visibility';
import { toReelBanner } from '../models/reel.model';
import { attractionsHighlightTitle, attractionsImages, attractionsMoreCount } from '../attractions/attraction-utils';
import {
  ACTIVE_ATTRACTIONS_BANNER_STYLE,
  ATTRACTIONS_MOSAIC_HEIGHT,
  AttractionsBannerStyle
} from '../attractions/attractions-mosaic-banner/attractions-mosaic-banner.component';

interface CategoryWithProducts {
  category: CategoryResponse;
  products: ProductResponse[];
}

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    animations: [
        fadeInOut,
        slideInOut,
        listAnimation,
        scaleInOut,
        bounceInOut,
        trigger('pageTransition', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('300ms ease-in', style({ opacity: 0 }))
            ])
        ]),
        trigger('slideDown', [
            state('hidden', style({
                transform: 'translateY(-100%)',
                opacity: 0,
                visibility: 'hidden'
            })),
            state('visible', style({
                transform: 'translateY(0)',
                opacity: 1,
                visibility: 'visible'
            })),
            transition('hidden => visible', [
                style({ visibility: 'visible' }),
                animate('300ms ease-out')
            ]),
            transition('visible => hidden', [
                animate('200ms ease-in'),
                style({ visibility: 'hidden' })
            ])
        ])
    ],
    standalone: false
})

export class MenuComponent implements OnInit, AfterViewInit, OnDestroy {
  clientId: string | null = null;
  tableId: string | null = null;
  clientToken: string | null = null;
  clientData: any = null;
  categories: CategoryResponse[] = [];
  products: ProductResponse[] = [];
  selectedCategoryId?: number;
  loading = false;
  isActionLoading = false;
  categorizedProducts: CategoryWithProducts[] = [];
  featuredProducts: ProductResponse[] = [];
  currencySymbol: string = '';
  showWaiterNotification = false;
  showReceiptNotification = false;
  showWaiterSuccess = false;
  showReceiptSuccess = false;
  currentCategory: number | null = null;
  private categoryElements: Map<number, HTMLElement> = new Map();
  private lastTrackedCategoryId: number | null = null;
  subcategoryParents: { [subId: number]: CategoryResponse } = {};
  private searchSubject = new Subject<string>();
  private previousScreen: string = 'default';
  private menuViewedTracked: boolean = false;
  @ViewChild('menuContainer', { static: false }) menuContainer!: ElementRef;
  cartItems: CartItem[] = [];
  searchQuery: string = '';
  searchResults: ProductResponse[] = [];
  showSearchResults: boolean = false;
  recentSearches: Set<string> = new Set();
  isSearchActive: boolean = false;
  isNavVisible = false;
  // Search bar is hidden over the hero and appears once the user scrolls away from the top.
  isSearchHeaderVisible = false;
  private lastScrollY = 0;
  private scrollThreshold = 50; // Reduced threshold for better UX
  private scrollDirection: 'up' | 'down' = 'down';
  currentBanner: BannerPromotion | null = null;
  showBanner: boolean = true;
  // Media loading state — drive the "blinking shadow" placeholders.
  heroImageLoaded = false;
  bannerImageLoaded = false;
  // Track loaded images by product id
  loadedImages: Set<number> = new Set();
  errorImages: Set<number> = new Set();
  catalogueIconError: boolean = false;
  currentLang: string = 'mk';
  availableLanguages: Language[] = [];
  comesFrom: string = 'main';
  branchIoLink: string = '';
  areCallActionsActive: boolean = true;
  orderingType: 'dine-in' | 'off-site' | 'delivery' = 'dine-in';
  hasActiveSubscription: boolean = true;
  menuLayoutSlug: string = 'tabs';
  rawCategories: ApiCategoryWithProducts[] = [];
  showUnavailableAlert: boolean = false;
  unavailableAlertMessage: string = '';
  private unavailableSub?: Subscription;
  /** Notification deep links (`?event=` / `?reel=1`) — see `handleNotificationDeepLinks`. */
  private deepLinkSub?: Subscription;
  private lastRefreshTime: number = 0;
  private readonly REFRESH_THRESHOLD = 2000; // 2 seconds
  isPushPromptOpen: boolean = false;
  /** Drives the prompt's copy — set together with `isPushPromptOpen`, never after it. */
  pushPromptSource: PushPromptSource | null = null;
  /** Pending 2s delay before the prompt opens — the diner can leave the menu inside that window. */
  private pushPromptTimer?: ReturnType<typeof setTimeout>;
  /** The dwell-earned fallback ask. The clock is GuestPushService's; this only opens the prompt. */
  private defaultAskSub?: Subscription;

  // region Tourist attractions (FE2-T168) — home floating button + banner state machine
  isDraggingAttractions = false;
  isOverAttractionsZone = false;
  /** Sticky once the button is released inside the drop zone; until then the banner is only a preview. */
  attractionsBannerCommitted = false;
  /**
   * Latched true the first time the user scrolls past 30% of the viewport; never set back to false.
   *
   * The latch is the point: re-evaluating the threshold on every scroll would take the banner away
   * again on the way back up, which is exactly the flicker Android's one-shot `first {}` avoids.
   * Until it fires, the menu shows no attractions UI at all — no banner and no puck either.
   *
   * Lives on the component, so it resets whenever the router recreates MenuComponent. That is only
   * safe because the *dismissal* outlives it in AttractionsEntryService: a user who has dismissed
   * the banner gets the puck back on return, not a re-armed banner.
   */
  attractionsScrollRevealed = false;

  /** Gates the whole feature: a touristic venue AND an available menu (subscription / special shop). */
  get showsAttractions(): boolean {
    return this.clientData?.is_touristic === true && this.hasActiveSubscription;
  }

  get attractionsCity(): string {
    return this.clientData?.attractions_city ?? '';
  }

  /**
   * The venue's attraction photos, cover first — the mosaic banner slices the whole list, the puck
   * and the classic banner take the first of it, so all three provably lead with the same photo.
   *
   * Memoised on the `clientData` reference rather than recomputed per call: MenuComponent runs
   * Default change detection, and a getter that allocates a fresh array every pass would hand the
   * mosaic's `*ngFor` a new identity on every tick. `clientData` is replaced wholesale on a shop
   * status refresh, so comparing the reference is enough to stay current.
   */
  private attractionsImagesFor: any = undefined;
  private attractionsImagesCache: string[] = [];
  get attractionsImages(): string[] {
    if (this.attractionsImagesFor !== this.clientData) {
      this.attractionsImagesFor = this.clientData;
      this.attractionsImagesCache = attractionsImages(this.clientData);
    }
    return this.attractionsImagesCache;
  }

  /** The single photo the puck and the classic banner lead with. */
  get attractionsLeadImage(): string | null {
    return this.attractionsImages[0] ?? null;
  }

  /**
   * The highlighted attraction's title for the mosaic banner, or null to fall back to
   * "Explore {city}". Cheap string work, so unlike `attractionsImages` it needs no memoisation —
   * it allocates nothing that change detection could see a new identity for.
   */
  get attractionsHighlightTitle(): string | null {
    return attractionsHighlightTitle(this.clientData);
  }

  /** How many other attractions there are besides the highlight — the banner's "and N more". */
  get attractionsMoreCount(): number {
    return attractionsMoreCount(this.clientData);
  }

  /**
   * Three ways the banner is on screen: revealed by scroll and not yet dismissed; re-opened by
   * dropping the puck in the bottom zone; or previewed live while a drag is in that zone.
   * Reversibility of the preview falls out for free — drag back above the line and
   * isOverAttractionsZone flips, so the banner and the puck swap straight back.
   */
  get showAttractionsBanner(): boolean {
    return this.showsAttractions &&
      ((this.attractionsScrollRevealed && !this.attractionsEntry.isDismissed) ||
        this.attractionsBannerCommitted ||
        (this.isDraggingAttractions && this.isOverAttractionsZone));
  }

  /**
   * The puck exists only *after* a dismissal — it is what the banner leaves behind, never a
   * cold-start surface.
   *
   * Deliberately keyed on `isDismissed` and NEVER on `!showAttractionsBanner`: the latter flips
   * mid-drag when the zone line is crossed, and an `*ngIf` on it would destroy the component holding
   * the live `setPointerCapture`, killing the drag-back-out reversal. `isDismissed` only ever
   * changes on a tap or the ✕, never while a finger is down. (Android needs a further
   * position-loaded gate because DataStore is async; localStorage is synchronous, so the puck here
   * cannot paint at the default and then jump.)
   */
  get showAttractionsFab(): boolean {
    return this.showsAttractions && this.attractionsEntry.isDismissed;
  }

  /**
   * Two banners, one slot — re-exposed from the module constant because a template can only resolve
   * members of its component class.
   */
  readonly attractionsBannerStyle: AttractionsBannerStyle = ACTIVE_ATTRACTIONS_BANNER_STYLE;

  /**
   * Lift the CLASSIC banner above the floating cart when it's visible; otherwise sit near the bottom
   * edge. Only the classic branch needs this: it is a floating card with side padding, whereas the
   * mosaic is full-bleed at `bottom: 0` and the cart moves above *it* instead (see `cartBottom`).
   * Kept so flipping the style constant back to 'classic' still produces a correct layout.
   */
  get attractionsBannerBottom(): number {
    return this.showsOrderBar ? 88 : 16;
  }

  /**
   * Whether the floating order bar is on screen — the same rule the bar itself applies.
   *
   * ⚠️ This is NOT `cartItems.length > 0` any more. The bar also shows for orders from
   * the last two hours with an empty cart, and the two layout getters that reserve room
   * for it have to agree with it, or the bar is drawn over the last product row on
   * exactly the visits this feature was added for.
   */
  private get showsOrderBar(): boolean {
    return shouldShowOrderBar(
      this.cartItems.length > 0,
      this.orderHistoryService.getRecentOrders(Date.now(), sessionStorage.getItem('clientId')).length > 0
    );
  }

  /**
   * The floating cart's distance from the bottom edge.
   *
   * Android inverts the web's original arrangement: the banner owns the bottom edge and the cart
   * rides above it, travelling on the same 280ms curve so the two move together rather than the cart
   * teleporting when the banner slides.
   *
   * Keyed on `showAttractionsBanner`, which flips immediately — deliberately NOT on a measured
   * height. The banner element stays in the DOM for the whole 280ms leave animation, so a
   * height-driven value would hold the cart lifted for the full exit and only then start to drop.
   * Measuring is unnecessary anyway: the mosaic is a fixed height by spec, and the classic banner
   * positions itself around the cart instead of the other way round.
   */
  get cartBottom(): number {
    return this.cartStacksOnBanner ? ATTRACTIONS_MOSAIC_HEIGHT + 8 : 20;
  }

  /**
   * The mosaic is full-bleed and its fixed height already spans the home-indicator strip, so when
   * the cart rides on top of it the safe-area inset must NOT be added again — `cartBottom` is
   * measured from the banner's top edge, not from the display edge.
   */
  get cartClearsSafeArea(): boolean {
    return !this.cartStacksOnBanner;
  }

  private get cartStacksOnBanner(): boolean {
    return this.showAttractionsBanner && this.attractionsBannerStyle === 'mosaic';
  }

  /**
   * Extra room under the last product card so it is not hidden behind the banner. The cart sits
   * *above* the banner, so the clearance is banner + gap + cart + margin — not just the banner.
   * Zero unless the banner is actually on screen, or every non-touristic venue's menu would end in
   * a screenful of dead space.
   *
   * The cart's slot is only booked when there IS a cart. Reserving it unconditionally left an empty
   * menu ending a cart's height above a banner with nothing in between — the same dead space this
   * getter returns '0px' to avoid on non-touristic venues. `.menu-bottom-spacer` animates its
   * margin on the same 280ms curve the cart slides in on, so the list closes up under the cart as
   * it arrives rather than jumping.
   */
  get attractionsBottomSpaceCss(): string {
    if (!this.cartStacksOnBanner) return '0px';
    const cartSlot = this.showsOrderBar ? 8 + 52 : 0;
    return `${ATTRACTIONS_MOSAIC_HEIGHT + cartSlot + 20}px`;
  }

  onAttractionsDragStateChanged(state: { dragging: boolean; overZone: boolean }): void {
    this.isDraggingAttractions = state.dragging;
    this.isOverAttractionsZone = state.overZone;
  }

  onAttractionsDropInZone(): void {
    this.attractionsBannerCommitted = true;
  }

  /**
   * Which surface the banner currently IS, for `entry_point`. Whether the mosaic or the classic
   * banner converts better is the question the two-styles-one-slot setup exists to answer, so the
   * style has to reach the event.
   */
  private get attractionsBannerEntry(): string {
    return this.attractionsBannerStyle === 'mosaic' ? AnalyticsEntry.MOSAIC : AnalyticsEntry.BANNER;
  }

  /** Takes the entry point because banner and puck share this handler, and which one converts is
   *  exactly what we need to know. */
  openAttractions(entryPoint: string = this.attractionsBannerEntry): void {
    this.analytics.log('attractions_open', { entry_point: entryPoint });
    this.attractionsBannerCommitted = false;
    // Acted on — coming back from the list should land on the puck, not on the banner the user has
    // already answered.
    this.attractionsEntry.dismissBanner();
    this.router.navigate(['/attractions']);
  }

  dismissAttractionsBanner(): void {
    // Dismissals are the only signal that the entry point is unwelcome; without them we would only
    // ever see the times it worked.
    this.analytics.log('attractions_entry_dismissed', { entry_point: this.attractionsBannerEntry });
    this.attractionsBannerCommitted = false;
    this.attractionsEntry.dismissBanner();
  }
  // endregion

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private cartService: CartService,
    private bannerService: BannerService,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private analytics: AnalyticsService,
    private branchService: BranchService,
    private cdr: ChangeDetectorRef,
    private currencyService: CurrencyService,
    private ngZone: NgZone,
    private shopStatusService: ShopStatusService,
    private guestPushService: GuestPushService,
    private productSheet: ProductSheetService,
    private attractionsEntry: AttractionsEntryService,
    public langSheet: LanguageSheetService,
    private reelService: ReelService,
    private reelPreload: ReelPreloadService,
    private location: Location,
    private deepLinks: NotificationDeepLinkService,
    private orderHistoryService: OrderHistoryService
  ) {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    
    // Set up search debounce mechanism
    this.searchSubject.pipe(
      debounceTime(1000), // Wait 1 second after user stops typing
      distinctUntilChanged() // Only emit if the search term has changed
    ).subscribe(searchTerm => {
      if (searchTerm.trim()) {
        this.trackSearchPerformed(searchTerm.trim());
      }
    });
    
    // Track navigation to determine previous screen
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event) => {
      this.trackPreviousScreen((event as NavigationEnd).url);
    });
  }

  ngOnInit(): void {
    this.clientId = sessionStorage.getItem('clientId');
    this.tableId = sessionStorage.getItem('tableId');
    this.clientToken = sessionStorage.getItem('authToken');
    const clientDataString = sessionStorage.getItem('clientData');
    
    // Get ordering type from sessionStorage
    const orderingType = sessionStorage.getItem('orderingType') as 'dine-in' | 'off-site' | 'delivery' | null;
    this.orderingType = orderingType || 'dine-in';

    // Check for stored button states
    this.checkStoredButtonStates();

    if (clientDataString) {
      try {
        this.clientData = JSON.parse(clientDataString);
        this.preloadHeroImage();
        // Check subscription status
        this.hasActiveSubscription =
          (this.clientData?.has_active_subscription === true) ||
          (this.clientData?.is_special_shop === true);
        // Read menu layout from client data
        this.menuLayoutSlug = this.clientData?.menu_layout?.slug || 'tabs';
        // Check call actions availability - only show for dine-in orders
        // Call actions should be active only if:
        // 1. Ordering type is 'dine-in'
        // 2. Client has call actions enabled via ft_call_actions flag
        this.updateCallActionsStatus();

        // Only load menu data if subscription is active
        if (this.hasActiveSubscription) {
          // Load cached data immediately for instant UI
          this.loadCachedData();
          
          // Refresh shop status (is_ordering_active, is_open_now, are_call_actions_active) on menu load (align with iOS)
          this.shopStatusService.refreshShopStatus(this.clientData.id).then(updated => {
            if (updated) {
              this.clientData = updated;
              this.updateCallActionsStatus();
              this.cdr.detectChanges();
            }
          });
          
          // Always refresh data in background
          this.refreshDataInBackground();
        } else {
          this.loading = false;
        }
        
      } catch {
        this.router.navigate(['']);
      }
    } else {
      this.router.navigate(['']);
    }

    const storedOrderId = localStorage.getItem('currentOrderId');
        
    if (storedOrderId) {
      this.cartService.setCurrentOrderId(parseInt(storedOrderId));
    } else {
      this.createInitialOrder();
    }

    this.cartService.cartItems$.subscribe(items => {
      this.cartItems = items;
    });

    this.unavailableSub = this.cartService.unavailableProduct$.subscribe(event => {
      if (event.from && event.to) {
        this.unavailableAlertMessage = this.translate.instant('menu.availability.availableFromTo', { from: event.from, to: event.to });
      } else {
        this.unavailableAlertMessage = this.translate.instant('menu.availability.notAvailableNow');
      }
      this.showUnavailableAlert = true;
    });

    // Initialize venue-based languages
    if (this.clientData?.id) {
      const shopId = this.clientData.id;
      const { needsSheet } = this.languageService.initializeForClient(shopId, this.clientData.languages);
      // First scan of a multilingual venue with no served device preference: offer the language
      // sheet over the (still-loading) menu. Deferred to the next microtask to avoid
      // ExpressionChangedAfterItHasBeenCheckedError against AppComponent's async-pipe host (see
      // product-details.component.ts), and guarded so a background refreshLanguages / component
      // re-init never re-prompts. Skipped when the menu itself is unavailable.
      if (needsSheet && this.hasActiveSubscription && !this.languageService.hasOffered(shopId)) {
        this.languageService.markOffered(shopId);
        Promise.resolve().then(() => this.langSheet.open('first'));
      }
    } else {
      this.availableLanguages = this.languageService.getBundledLanguages();
    }
    this.currentLang = this.languageService.getCurrentLang();

    this.languageService.venueLanguages$.subscribe(langs => {
      if (langs.length > 0) this.availableLanguages = langs;
    });

    // Subscribe to language changes
    this.languageService.currentLang$.subscribe(lang => {
      this.currentLang = lang;
      // Re-initialize category elements when language changes
      setTimeout(() => this.initializeCategoryElements(), 100);
    });

    // Subscribe to currency changes
    this.currencyService.currencySymbol$.subscribe(symbol => {
      this.currencySymbol = symbol;
    });

    this.analytics.log('menu_presented')

    // Initialize Branch deep link for banner install button
    this.branchService.createBranchDeepLink().subscribe({
      next: (url: string) => this.branchIoLink = url
    });

    // Push notifications — runs on every menu load (every QR scan)
    this.initPushNotifications();

    // A tapped touristic notification lands here first and then asks for a screen on top.
    this.handleNotificationDeepLinks();
  }

  /**
   * The two destinations a touristic notification reaches THROUGH the menu, as query parameters on
   * `/menu`: `?event=<id>` for the event notification and `?reel=1` for the Gulli one.
   *
   * Both are deliberately not routes of their own. The guest asked for a notification from a venue,
   * so the venue's menu is what should be underneath when they dismiss whatever opened on top of it —
   * and the reel has no route to give: it is an overlay that the home banner opens in place
   * (`handleBannerClick`), so a notification can only reach it by landing on the menu and doing the
   * same thing the banner does.
   *
   * A subscription rather than a snapshot read, because the warm path re-uses this component: a tap
   * while the app is already open on `/menu` changes only the query string, and Angular keeps the
   * component instance — `ngOnInit` never runs a second time, so a snapshot would see nothing.
   */
  private handleNotificationDeepLinks(): void {
    // Unsubscribed on destroy, unlike most of this component's subscriptions, because this one acts
    // on the router rather than on a field: an instance left listening after the guest has navigated
    // away would navigate on their behalf from a screen it no longer owns.
    this.deepLinkSub = this.route.queryParamMap.subscribe(params => {
      const eventId = (params.get('event') || '').trim();
      const wantsReel = params.get('reel') === '1';
      if (!eventId && !wantsReel) return;

      // Once per page load, however the history stack ends up looking — see the service.
      const key = eventId ? `menu:event=${eventId}` : 'menu:reel=1';
      if (!this.deepLinks.consume(key)) return;

      // Deferred one tick: this fires while the navigation that is activating this component is
      // still in flight, and everything below is itself router work.
      setTimeout(() => this.openFromNotification(eventId, wantsReel), 0);
    });
  }

  private async openFromNotification(eventId: string, wantsReel: boolean): Promise<void> {
    // Strip the parameter with `Location`, DELIBERATELY NOT with a router navigation.
    //
    // A `router.navigate([], { queryParams: {} })` does the same thing to the address bar but emits
    // NavigationStart on the way, and on a first visit to a multilingual venue that is fatal: the
    // language sheet has just opened on a microtask, `LanguageSheetService` tears itself down on
    // NavigationStart, and `markOffered` has already been spent — so the guest silently loses the
    // one chance to pick a language on this page load. Rewriting the URL directly touches no
    // overlay, and `NotificationDeepLinkService` (not the stripped parameter) is what stops this
    // from firing twice.
    this.location.replaceState('/menu');

    if (eventId) {
      this.router.navigate(['/event', eventId]);
      return;
    }

    if (!this.clientData?.id) return;

    // Cached per session, so this is usually the banner the menu has already fetched.
    const banner = await this.bannerService.fetchActiveBanner(this.clientData.id);
    if (!banner || banner.type !== 'media') return;

    const reelBanner = toReelBanner(banner);
    if (!reelBanner) return;

    // Same event as a tap on the banner, with the entry point telling the two apart — a reel opened
    // from a notification is the feature working, and it would otherwise be indistinguishable from
    // one the diner found on their own.
    this.analytics.log('banner_open', {
      banner_id: banner.id,
      banner_type: banner.type,
      entry_point: AnalyticsEntry.DEEPLINK
    });
    this.reelService.open(reelBanner);
  }

  private async initPushNotifications(): Promise<void> {
    const result = await this.guestPushService.handleMenuOpen();

    if (result === 'scheduled') {
      // Returning user, already granted — notification silently scheduled
      console.log('Push: silently scheduled re-engagement');
      return;
    }

    // Navigating back to the menu re-creates this component; the scan for this session was already
    // registered, so there is nothing to schedule.
    if (result === 'already_scheduled') return;

    if (result === 'needs_prompt') {
      this.schedulePushPrompt();
      this.listenForDefaultAsk();
    }
  }

  /**
   * Opens the permission prompt 2s after the diner returns to the menu having just browsed Explore
   * or Events — an ask backed by demonstrated interest, rather than the old 5s timer that fired at
   * every diner who had merely loaded the menu.
   *
   * Nothing here coordinates with the first-visit language sheet any more, and nothing needs to: the
   * trigger is an arrival at /menu that FOLLOWS a visit to another screen, by which point a
   * first-load sheet is long gone. GuestPushService owns both the engagement record and the budget,
   * because the router rebuilds this component on every one of those arrivals.
   */
  private schedulePushPrompt(): void {
    const source = this.guestPushService.pendingPromptSource();
    if (!source || !this.guestPushService.shouldShowPrompt(source)) return;

    this.pushPromptTimer = setTimeout(() => {
      // Re-checked at fire time: 2s is long enough for the reason to ask to disappear, e.g. a
      // permission granted in another tab on the same venue.
      if (!this.guestPushService.shouldShowPrompt(source)) return;
      // Spends the source's lifetime slot and this visit's single ask, and logs the impression.
      this.guestPushService.markPromptShown(source);
      // Set before `isPushPromptOpen`, so the prompt's first render already has the right copy and
      // never flashes the generic fallback.
      this.pushPromptSource = source;
      this.isPushPromptOpen = true;
    }, 2000);
  }

  /**
   * The fallback ask, for the diner who browses the menu and never opens Explore or Events.
   *
   * No timer here on purpose. The dwell it is earned by has to survive this component — a diner who
   * opens the cart and comes back has not restarted their meal — so GuestPushService counts it and
   * this only listens. Everything that could refuse the ask has already been checked when it emits;
   * re-checking `shouldShowPrompt` here would just race the emission that passed it a tick ago.
   */
  private listenForDefaultAsk(): void {
    this.defaultAskSub?.unsubscribe();
    this.defaultAskSub = this.guestPushService.defaultAskDue$.subscribe(() => {
      this.guestPushService.markPromptShown('default');
      this.pushPromptSource = 'default';
      this.isPushPromptOpen = true;
    });
  }

  onPushPromptAccepted(): void {
    this.isPushPromptOpen = false;
  }

  onPushPromptDismissed(): void {
    this.isPushPromptOpen = false;
  }

  private checkStoredButtonStates() {
    // Check waiter button state
    const waiterDisabledUntil = localStorage.getItem('waiterDisabledUntil');
    if (waiterDisabledUntil) {
      const disabledTime = parseInt(waiterDisabledUntil);
      if (Date.now() < disabledTime) {
        this.showWaiterSuccess = true;
        // Set up timer to re-enable
        setTimeout(() => {
          this.showWaiterSuccess = false;
          localStorage.removeItem('waiterDisabledUntil');
        }, disabledTime - Date.now());
      } else {
        localStorage.removeItem('waiterDisabledUntil');
      }
    }

    // Check receipt button state
    const receiptDisabledUntil = localStorage.getItem('receiptDisabledUntil');
    if (receiptDisabledUntil) {
      const disabledTime = parseInt(receiptDisabledUntil);
      if (Date.now() < disabledTime) {
        this.showReceiptSuccess = true;
        // Set up timer to re-enable
        setTimeout(() => {
          this.showReceiptSuccess = false;
          localStorage.removeItem('receiptDisabledUntil');
        }, disabledTime - Date.now());
      } else {
        localStorage.removeItem('receiptDisabledUntil');
      }
    }
  }

  private loadCachedData(): void {
    const cachedCategories = sessionStorage.getItem('menuCategories');
    const cachedProducts = sessionStorage.getItem('menuProducts');
    const cachedFeatured = sessionStorage.getItem('featuredProducts');
    // Load cached data immediately
    if (cachedCategories) {
      this.categories = JSON.parse(cachedCategories);
    }
    if (cachedProducts) {
      this.products = JSON.parse(cachedProducts);
    }
    if (cachedFeatured) {
      this.featuredProducts = JSON.parse(cachedFeatured);
    }
    // Always derive the currency from THIS shop's clientData, never from a
    // possibly-stale cached currencySymbol (which could belong to a previous
    // shop after switching shops in the same tab). loadCurrency updates both
    // the observable and the cached symbol.
    if (this.clientData) {
      this.currencyService.loadCurrency(this.clientData);
    }
    const cachedRaw = sessionStorage.getItem('rawCategories');
    if (cachedRaw) {
      this.rawCategories = JSON.parse(cachedRaw);
      this.cartService.buildAvailabilityMap(this.rawCategories);
      this.cartService.buildUnavailableInMenuSet(this.featuredProducts, this.rawCategories);
    }

    // If we have cached data, show it immediately
    if (cachedCategories && cachedProducts) {
      this.groupProductsByCategory();
      this.checkActiveBanner();
    } else {
      // No cached data, load from API (this will show loading state).
      // Currency is already derived from clientData above.
      this.loadFullMenu();
    }
  }

  /**
   * Performs the actual data refresh (client, menu, languages) and forces UI update.
   * Used by both manualRefresh (pull-to-refresh) and refreshDataInBackground (initial load).
   * Each step is guarded so one failure (e.g. Firestore CORS) doesn't block the menu refresh.
   */
  private async performDataRefresh(): Promise<void> {
    if (!this.clientData?.id) return;

    try {
      await this.refreshClientData();
    } catch {
      // Continue with menu refresh even if client data fails
    }
    this.updateCallActionsStatus();
    this.refreshLanguages();

    try {
      await this.refreshFullMenu();
    } catch {
      // Refresh failed, keep existing data
    }

    try {
      // Re-check the backend for a new / updated / removed banner. Forces a
      // cache-bypass fetch so it refreshes like the other data above (the banner
      // cache otherwise survives reloads and would mask a changed banner).
      await this.checkActiveBanner(true);
    } catch {
      // Banner refresh failed, keep existing banner
    }
    // Always run change detection so mobile browsers update the view
    this.ngZone.run(() => {
      this.cdr.markForCheck();
      setTimeout(() => {
        this.cdr.detectChanges();
        setTimeout(() => this.cdr.detectChanges(), 50);
      }, 0);
    });
  }

  /**
   * Refreshes data in background after page load.
   * On every page load (including pull-to-refresh), we check if cached data is stale and refresh if needed.
   */
  private async refreshDataInBackground(): Promise<void> {
    if (!this.clientData) return;

    const hasCachedData = sessionStorage.getItem('menuCategories') && sessionStorage.getItem('menuProducts');
    if (!hasCachedData) return;

    const cachedDataTimestamp = sessionStorage.getItem('menuDataTimestamp');
    if (!cachedDataTimestamp) {
      await this.performDataRefresh();
      return;
    }

    const timeSinceCache = Date.now() - parseInt(cachedDataTimestamp, 10);
    const CACHE_MAX_AGE = 30 * 1000; // 30 seconds
    if (timeSinceCache > CACHE_MAX_AGE) {
      await this.performDataRefresh();
    }
  }

  private async refreshFullMenu(): Promise<void> {
    if (!this.clientData?.id) return;

    try {
      const menuResponse = await this.apiService.getFullMenu(this.clientData.id).toPromise();
      
      if (menuResponse) {
        // Process response inside Angular zone
        this.ngZone.run(() => {
          this.processMenuResponse(menuResponse);
          this.cdr.markForCheck();
        });
      }
    } catch {
      // Refresh failed, keep existing data
    }
  }

  /**
   * Loads the full menu (categories with embedded products + featured products) in a single API call.
   */
  loadFullMenu() {
    if (!this.clientData?.id) return;

    this.loading = true;
    this.apiService.getFullMenu(this.clientData.id).subscribe({
      next: (menuResponse) => {
        this.ngZone.run(() => {
          this.processMenuResponse(menuResponse);
          this.loading = false;
          this.cdr.markForCheck();
          setTimeout(() => this.cdr.detectChanges(), 0);
        });
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  /**
   * Processes the full menu response: extracts categories, products, and featured products.
   */
  private processMenuResponse(menuResponse: FullMenuResponse): void {
    // Extract and sort categories
    if (menuResponse.categories) {
      const hasProducts = (cat: ApiCategoryWithProducts): boolean => {
        if (cat.products && cat.products.length > 0) return true;
        if (cat.subcategories) {
          return cat.subcategories.some(sub => (sub.products?.length ?? 0) > 0);
        }
        return false;
      };

      const nonEmpty = menuResponse.categories.filter(hasProducts);
      const sorted = nonEmpty.sort((a: any, b: any) => {
        const aOrder = a.menu_order ?? 0;
        const bOrder = b.menu_order ?? 0;
        if (aOrder === 0 && bOrder === 0) return 0;
        if (aOrder === 0) return 1;
        if (bOrder === 0) return -1;
        return aOrder - bOrder;
      });

      // Store raw categories (with subcategories) for browse layout and availability
      this.rawCategories = sorted;
      sessionStorage.setItem('rawCategories', JSON.stringify(this.rawCategories));

      this.categories = sorted
        .map(({ products, subcategories, available_from, available_to, ...category }) => category as CategoryResponse);
      sessionStorage.setItem('menuCategories', JSON.stringify(this.categories));

      // Flatten and deduplicate products from all categories (including subcategories)
      const productSet = new Map<number, ProductResponse>();
      for (const cat of menuResponse.categories) {
        if (cat.products) {
          for (const product of cat.products) {
            if (!productSet.has(product.id)) {
              productSet.set(product.id, product);
            }
          }
        }
        if (cat.subcategories) {
          for (const sub of cat.subcategories) {
            if (sub.products) {
              for (const product of sub.products) {
                if (!productSet.has(product.id)) {
                  productSet.set(product.id, product);
                }
              }
            }
          }
        }
      }
      this.products = Array.from(productSet.values());
      sessionStorage.setItem('menuProducts', JSON.stringify(this.products));

      // Build product availability map
      this.cartService.buildAvailabilityMap(this.rawCategories);
    }

    // Extract featured products
    if (menuResponse.featured_products) {
      this.featuredProducts = menuResponse.featured_products;
      sessionStorage.setItem('featuredProducts', JSON.stringify(this.featuredProducts));
    }

    this.cartService.buildUnavailableInMenuSet(this.featuredProducts, this.rawCategories);

    this.groupProductsByCategory();
    this.checkActiveBanner();
  }

  private groupProductsByCategory() {
    const stripHierarchyFields = (cat: ApiCategoryWithProducts): CategoryResponse => {
      const { products, subcategories, available_from, available_to, ...rest } = cat;
      return rest as CategoryResponse;
    };

    const newCategorizedProducts: CategoryWithProducts[] = [];
    const newSubcategoryParents: { [subId: number]: CategoryResponse } = {};
    for (const rawCat of this.rawCategories) {
      const parentBase = stripHierarchyFields(rawCat);
      const parentDirect = rawCat.products ?? [];

      if (parentDirect.length > 0) {
        newCategorizedProducts.push({
          category: { ...parentBase },
          products: [...parentDirect],
        });
      }

      const sortedSubs = [...(rawCat.subcategories ?? [])].sort((a: any, b: any) => {
        const aOrder = a.menu_order ?? 0;
        const bOrder = b.menu_order ?? 0;
        if (aOrder === 0 && bOrder === 0) return 0;
        if (aOrder === 0) return 1;
        if (bOrder === 0) return -1;
        return aOrder - bOrder;
      });
      for (const sub of sortedSubs) {
        const subProducts = sub.products ?? [];
        if (subProducts.length === 0) continue;
        const subBase = stripHierarchyFields(sub);
        newCategorizedProducts.push({
          category: { ...subBase },
          products: [...subProducts],
        });
        if (subBase.id != null) {
          newSubcategoryParents[subBase.id] = parentBase;
        }
      }
    }

    this.categorizedProducts = [...newCategorizedProducts];
    this.subcategoryParents = newSubcategoryParents;
  }

  getTabLabel(categoryGroup: CategoryWithProducts): string {
    const subId = categoryGroup.category.id;
    const parent = subId != null ? this.subcategoryParents[subId] : undefined;
    const subLabel = this.getCategoryDisplayName(categoryGroup.category, this.currentLang);
    if (!parent) return subLabel;
    const parentLabel = this.getCategoryDisplayName(parent, this.currentLang);
    return parentLabel ? `${parentLabel} - ${subLabel}` : subLabel;
  }

  onCategoryTapped(category: ApiCategoryWithProducts): void {
    if (category.id) {
      this.router.navigate(['/category', category.id]);
    }
  }

  filterProductsByCategory(categoryId: number) {
    this.selectedCategoryId = categoryId;
    return this.products.filter(product =>
      product.categories.some(cat => cat.id === categoryId)
    );
  }

  onProductClick(productId: number) {
    // Find the product to get the name
    const product = this.products.find(p => p.id === productId);
    const productName = product ? product.name : 'Unknown Product';
    
    // Determine entry screen based on current state
    let entryScreen = 'menu';
    if (this.isSearchActive) {
      entryScreen = 'search';
    }
    
    this.analytics.log('product_open', {
      product_id: productId.toString(),
      product_name: productName,
      entry_screen: entryScreen
    })
    this.productSheet.open(productId);
  }

  loadCurrency() {
    this.currencyService.loadCurrency(this.clientData);
  }

  getSecondPrice(price: string | number): string | null {
    return this.currencyService.getSecondPrice(price, this.clientData);
  }

  getSecondCurrencySymbol(): string | null {
    return this.currencyService.getSecondCurrencySymbol(this.clientData);
  }

  isSecondCurrencyActive(): boolean {
    return this.currencyService.isSecondCurrencyActive(this.clientData);
  }

  showWaiterToast() {
    this.showWaiterNotification = true;
    setTimeout(() => {
      this.showWaiterNotification = false;  
      this.showWaiterSuccess = true;
      
      // Store disabled state with expiration time
      const disabledUntil = Date.now() + (5 * 60 * 1000);
      localStorage.setItem('waiterDisabledUntil', disabledUntil.toString());
      
      // Re-enable the button after 5 minutes
      setTimeout(() => {
        this.showWaiterSuccess = false;
        localStorage.removeItem('waiterDisabledUntil');
      }, 5 * 60 * 1000);
    }, 3000);
  }

  showReceiptToast() {
    this.showReceiptNotification = true;
  }

  /**
   * The two ways to settle a bill, for the shared payment sheet.
   *
   * The `method` values are the canonical payment-method strings rather than the
   * `receipt-*` action names, because the sheet picks each button's artwork from them.
   * They are translated back to actions in onBillPaymentChosen().
   */
  readonly billPaymentOptions: PaymentSheetOption[] = [
    { method: 'cash_on_delivery', labelKey: 'menu.actions.requestBill.modal.cashButton' },
    { method: 'card_on_delivery', labelKey: 'menu.actions.requestBill.modal.cardButton' },
  ];

  /**
   * ⚠️ The `receipt-cash` / `receipt-card` strings are the waiter-notification contract
   * and must not drift — the venue's app reads them to know what to bring to the table.
   *
   * An unrecognised method sends NOTHING rather than falling through to card. Telling a
   * waiter the wrong thing about how someone is paying is worse than telling them
   * nothing, and this is the exact shape of the bug that put every Hype order across as
   * Card.
   */
  onBillPaymentChosen(method: string): void {
    const action = method === 'cash_on_delivery' ? 'receipt-cash'
                 : method === 'card_on_delivery' ? 'receipt-card'
                 : null;

    if (action === null) {
      return;
    }

    this.callAction(action);
  }

  async callAction(actionType: 'waiter' | 'receipt-cash' | 'receipt-card') {
    if ((actionType === 'waiter' && this.showWaiterSuccess) || 
        ((actionType === 'receipt-cash' || actionType === 'receipt-card') && this.showReceiptSuccess)) {
      return;
    }

    // Refresh shop status (ordering, working hours, call actions) before performing action (align with iOS)
    const updated = await this.shopStatusService.refreshShopStatus(this.clientData.id);
    if (updated) {
      this.clientData = updated;
    }
    this.updateCallActionsStatus();
    
    // Check if call actions are active
    if (!this.areCallActionsActive) {
      // Call actions are disabled, don't proceed
      return;
    }

    this.isActionLoading = true;
    try {
      await this.notificationService.sendWaiterNotification(this.tableId!, actionType);
      
      if(actionType === 'waiter'){
        this.analytics.log('waiter_called')
        this.showWaiterToast();
      } else if(actionType === 'receipt-cash' || actionType === 'receipt-card'){
        this.analytics.log('receipt_called')
        this.showReceiptSuccess = true;
        this.showReceiptNotification = false;
        const disabledUntil = Date.now() + (5 * 60 * 1000);
        localStorage.setItem('receiptDisabledUntil', disabledUntil.toString());
        
        setTimeout(() => {
          this.showReceiptSuccess = false;
          localStorage.removeItem('receiptDisabledUntil');
        }, 5 * 60 * 1000);
      }
    } catch {
      // Notification failed
    } finally {
      this.isActionLoading = false;
    }
  }

  private initializeCategoryElements() {
    this.categoryElements.clear();

    const sections = Array.from(document.querySelectorAll('.menu-category[data-category-id]'));
    for (const section of sections) {
      const idAttr = (section as HTMLElement).dataset['categoryId'];
      const id = idAttr ? Number(idAttr) : NaN;
      if (!Number.isNaN(id)) {
        this.categoryElements.set(id, section as HTMLElement);
      }
    }

    if (this.categoryElements.size < this.categorizedProducts.length) {
      setTimeout(() => this.initializeCategoryElements(), 100);
    }
  }

  private onScroll = () => {
    // Only proceed if we have category elements initialized
    if (this.categoryElements.size === 0) {
      this.initializeCategoryElements();
      return;
    }

    requestAnimationFrame(() => {
      const navHeight = document.querySelector('.sticky-nav')?.clientHeight || 0;
      const scrollPosition = window.scrollY + navHeight;
      const categoryNav = document.querySelector('.category-nav') as HTMLElement;

      if (!categoryNav) return;

      // Find the category that's currently in view
      for (const [categoryId, element] of this.categoryElements) {
        const rect = element.getBoundingClientRect();
        const elementTop = window.scrollY + rect.top - navHeight;
        const elementBottom = elementTop + rect.height;

        if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
          if (this.currentCategory !== categoryId) {
            this.currentCategory = categoryId;

            // Fire analytics event for category viewed
            this.trackCategoryViewed(categoryId);
            
            // Find the active nav item and scroll it into view
            const activeNavItem = categoryNav.querySelector(`.nav-item.active`) as HTMLElement;
            if (activeNavItem) {
              const navRect = categoryNav.getBoundingClientRect();
              const activeRect = activeNavItem.getBoundingClientRect();

              // Calculate the scroll position to center the active item
              const navItems = Array.from(categoryNav.querySelectorAll('.nav-item'));
              const activeIndex = navItems.indexOf(activeNavItem);
              const isFirstItem = activeIndex === 1;
              const scrollLeft = activeNavItem.offsetLeft - (navRect.width / 2) + (activeRect.width / 2) + (isFirstItem ? -16 : 24);
              
              // Smooth scroll to the position
              categoryNav.scrollTo({
                left: scrollLeft,
                behavior: 'smooth'
              });
            }
          }
          break;
        }
      }
    });
  }

  ngAfterViewInit() {
    // Initial attempt to initialize category elements
    this.initializeCategoryElements();
    
    // Remove any existing scroll listeners to prevent duplicates
    window.removeEventListener('scroll', this.onScroll);
    // Add scroll listener with proper binding
    window.addEventListener('scroll', this.onScroll, { passive: true });
    
    // Set up visibility detection for menu viewed tracking
    this.setupVisibilityDetection();
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('scroll', this.handleScroll);
    this.searchSubject.complete();
    this.unavailableSub?.unsubscribe();
    this.deepLinkSub?.unsubscribe();
    // Leaving the menu inside the 2s window cancels the ask outright: it would otherwise spend a
    // lifetime slot and open a prompt on a component the router has already torn down.
    clearTimeout(this.pushPromptTimer);
    // Only the listener goes. The dwell clock behind it keeps running on the service, paused by
    // `isMenuInView` until the diner is back on the menu — which is the whole point of it living
    // there rather than here.
    this.defaultAskSub?.unsubscribe();
  }

  scrollToCategory(categoryId: number) {
    if (this.categoryElements.size === 0) {
      this.initializeCategoryElements();
    }

    const element = this.categoryElements.get(categoryId);
    if (element) {
      this.trackCategorySelected(categoryId);
      // A deliberate move through the menu, and one a diner can make without ever scrolling far
      // enough to trip the 30% latch above.
      this.guestPushService.noteMenuInteraction();

      const navHeight = document.querySelector('.sticky-nav')?.clientHeight || 0;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: elementPosition - navHeight,
        behavior: 'smooth'
      });
    }
  }

  addToCart(event: Event, product: ProductResponse) {
    event.stopPropagation(); 
    
    // Track search result interaction if we're in search mode
    if (this.isSearchActive && this.searchQuery.trim()) {
      this.trackSearchResultClicked(product.id, product.name, this.searchQuery);
    }
    
    // Determine source based on current state
    let source = 'main';
    if (this.isSearchActive) {
      source = 'search';
    }
    
    this.cartService.addToCart(product, source);
  }

  isInCart(productId: number): boolean {
    return this.cartService.getProductQuantity(productId) > 0;
  }

  /**
   * The quantity of a dish across EVERY line it occupies.
   *
   * The old `cartItems.find(i => i.id === productId)` answered with whichever line came first, so a
   * guest holding two cheese pizzas and one bacon pizza was told they had two. A product id no
   * longer identifies a row — the sum is the only honest answer a product-keyed question has.
   */
  getItemQuantity(productId: number): number {
    return this.cartService.getProductQuantity(productId);
  }

  updateQuantity(event: Event, product: ProductResponse, change: number): void {
    event.stopPropagation();
    
    // Track search result interaction if we're in search mode
    if (this.isSearchActive && this.searchQuery.trim()) {
      this.trackSearchResultClicked(product.id, product.name, this.searchQuery);
    }
    
    const currentQuantity = this.getItemQuantity(product.id);
    if (currentQuantity + change <= 0) {
      // Determine source based on current state
      let source = 'menu';
      if (this.isSearchActive) {
        source = 'search';
      }
      this.cartService.removeFromCart(product.id, source);
    } else {
      // Determine change source based on current state
      let changeSource = 'menu';
      if (this.isSearchActive) {
        changeSource = 'search';
      }
      this.cartService.updateQuantity(product.id, currentQuantity + change, changeSource);
    }
  }

  /**
   * A tile adjusted one specific add-on line by itself, so `updateQuantity` above never ran.
   *
   * The cart is already correct — the tile owns the 0/1/2+ routing precisely so the branch is not
   * copied into every host. What is left here is the search bookkeeping that hung off the stepper,
   * which would otherwise quietly stop being reported the moment a venue enabled add-ons.
   */
  onTileCartAdjusted(productId: number): void {
    if (!this.isSearchActive || !this.searchQuery.trim()) return;
    const product = this.products.find(p => p.id === productId);
    this.trackSearchResultClicked(productId, product ? product.name : 'Unknown Product', this.searchQuery);
  }

  /**
   * A tile's stepper routed to the add-on picker instead of touching the cart.
   *
   * Deliberately NOT `onSearchResultClick`: that handler clears `searchQuery` and hides the result
   * list, which is right for a tap on the card but would leave a guest who tapped `+` staring at an
   * empty search screen once the sheet closed. Present the dish, keep the results — and keep the
   * search bookkeeping the pre-add-ons `+` did through `addToCart`, so enabling add-ons does not
   * silently drop `menu_search_result_clicked` for the dishes that have them.
   */
  onTileSheetRequested(productId: number): void {
    this.onTileCartAdjusted(productId);
    this.onProductClick(productId);
  }

  private async createInitialOrder() {
    if (!this.clientData?.id) return;
    
    try {
      const orderResponse = await this.apiService.createOrder(this.clientData.id).toPromise();
      if (orderResponse && orderResponse.id) {
        this.cartService.setCurrentOrderId(orderResponse.id);
      }
    } catch {
      // Order creation failed
    }
  }

  onSearchFocus() {
    this.analytics.log('menu_search_initiated')
    this.isSearchActive = true;
    this.showSearchResults = true;
    
    this.updatePreviousScreenForSearch();
  }

  closeSearch() {
    this.analytics.log('menu_search_cancel')
    this.isSearchActive = false;
    this.showSearchResults = false;
    this.searchQuery = '';
    
    // Reset previous screen when exiting search mode
    this.previousScreen = 'default';
  }

  onSearchInput(event: any) {
    this.searchQuery = event.target.value;
    
    // Emit to search subject for debounced analytics tracking
    this.searchSubject.next(this.searchQuery);
    
    // Immediate search results update for UI responsiveness
    if (this.searchQuery.trim()) {
      this.searchResults = this.products.filter(product =>
        product.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    } else {
      this.searchResults = [];
    }
  }

  onSearchResultClick(productId: number) {
    // Add the clicked product name to recent searches
    const product = this.products.find(p => p.id === productId);
    if (product) {
      this.recentSearches.add(product.name);
      if (this.recentSearches.size > 5) {
        // Keep only the 5 most recent searches
        this.recentSearches = new Set(Array.from(this.recentSearches).slice(-5));
      }
      
      // Track search result click
      this.trackSearchResultClicked(productId, product.name, this.searchQuery);
    }
    
    // Clear search and navigate
    this.searchQuery = '';
    this.showSearchResults = false;
    this.onProductClick(productId);
  }

  getCategoryForProduct(productId: number): string {
    const product = this.products.find(p => p.id === productId);
    if (product && product.categories.length > 0) {
      const category = this.categories.find(c => c.id === product.categories[0].id);
      return category ? this.getCategoryDisplayName(category, this.currentLang) : 'Uncategorized';
    }
    return 'Uncategorized';
  }

  /**
   * Arrow property, not a method: `addEventListener`/`removeEventListener` must be handed the *same*
   * reference, and `this.handleScroll.bind(this)` returns a fresh function each call — which is why
   * the removal in ngOnDestroy used to be a no-op and every MenuComponent instance leaked a live
   * listener across navigations to /attractions and back.
   */
  private handleScroll = () => {
    requestAnimationFrame(() => {
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;

      // The attractions banner is the feature's primary entry point and it is revealed by scrolling:
      // the user browses, and it rises from the bottom edge. 30% of the viewport, matching Android.
      //
      // Latched on purpose — once true it is never set back, so scrolling back to the top does not
      // take the banner away again.
      if (!this.attractionsScrollRevealed && currentScrollY > window.innerHeight * 0.3) {
        this.attractionsScrollRevealed = true;
        // The latch is already one-shot, so this fires exactly once per page load — which is what
        // an impression should be.
        if (this.showsAttractions) {
          this.analytics.log('attractions_entry_shown', { entry_point: this.attractionsBannerEntry });
        }
        // Same threshold, deliberately: "scrolled past 30% of the viewport" is already this app's
        // definition of a diner who is browsing rather than glancing, and the fallback push ask
        // wants exactly that. The service's flag outlives this latch, which resets per instance.
        this.guestPushService.noteMenuInteraction();
      }

      // Determine scroll direction
      this.scrollDirection = currentScrollY > this.lastScrollY ? 'down' : 'up';

      // Update visibility based on scroll position and direction
      if (currentScrollY > this.scrollThreshold) {
        // Show nav when scrolling up or when we're far down the page
        this.isNavVisible = this.scrollDirection === 'up' || currentScrollY > 200;
      } else {
        this.isNavVisible = false;
      }

      // Search bar shows only once scrolled away from the top (hidden over the hero).
      this.isSearchHeaderVisible = currentScrollY > this.scrollThreshold;

      this.lastScrollY = currentScrollY;
    });
  };

  private async checkActiveBanner(forceRefresh = false) {
    if (this.clientData?.id) {
      const previousBannerId = this.currentBanner?.id ?? null;
      this.currentBanner = await this.bannerService.fetchActiveBanner(this.clientData.id, forceRefresh);

      // Only report the impression / warm the reel when the banner actually changed, so a
      // data refresh that returns the same banner doesn't double-count banner_shown.
      if (this.currentBanner && this.currentBanner.id !== previousBannerId) {
        // New banner image → show the blinking-shadow placeholder until it loads.
        this.bannerImageLoaded = false;
        // `banner_type` matters because a media banner an older build cannot open still renders and
        // still reports `banner_shown` — without the type, a dead banner and a working one are
        // indistinguishable in the engagement funnel.
        this.analytics.log('banner_shown', {
          banner_id: this.currentBanner.id,
          banner_type: this.currentBanner.type
        });

        // Warm the first reel clip so the media banner opens fast when tapped (iOS parity:
        // triggerReelPreloadIfNeeded). Idempotent per banner across the cached + network paths.
        if (this.currentBanner.type === 'media') {
          this.reelPreload.warm(toReelBanner(this.currentBanner));
        }
      }
    }
  }

  handleBannerClick() {
    
    if (!this.currentBanner) return;

    this.analytics.log('banner_open', {
      banner_id: this.currentBanner.id,
      banner_type: this.currentBanner.type
    })

    switch (this.currentBanner.type) {
      case 'event':
        if (this.currentBanner.eventId) {
          this.router.navigate(['/event', this.currentBanner.eventId]);
        }
        break;
      case 'product':
        if (this.currentBanner.productId) {
          const product = this.products.find(p => p.id === this.currentBanner?.productId);
          if (product) {
            if (this.canEnableOrdering() && this.cartService.isProductCurrentlyAvailable(product.id)) {
              this.analytics.log('promo_product_added', {
                product_id: String(product.id)
              });
              this.cartService.addToCart(product, 'main');
            } else {
              this.onProductClick(product.id);
            }
          }
        }
        break;
      case 'navigationLink':
        if (this.currentBanner.navigationUrl) {
          window.open(this.currentBanner.navigationUrl, '_blank');
        }
        break;
      case 'media': {
        // Open the full-screen Feather Reel. An empty-clip banner is inert (ReelService.open guards).
        const reelBanner = toReelBanner(this.currentBanner);
        if (reelBanner) {
          this.reelService.open(reelBanner);
        }
        break;
      }
    }
  }

  trackByCategory(index: number, categoryGroup: any): any {
    return categoryGroup.category.id;
  }

  trackByProduct(index: number, product: any): any {
    return product.id;
  }

  onImageLoad(productId: number) {
    this.loadedImages.add(productId);
  }

  onImageError(productId: number) {
    this.errorImages.add(productId);
  }

  isImageLoaded(productId: number): boolean {
    return this.loadedImages.has(productId);
  }

  isImageError(productId: number): boolean {
    return this.errorImages.has(productId);
  }
  getProductImage(product: any): string {
    const fallback = '../../assets/dish 3.png';
    const image = product?.images?.[0]?.src;
    return image || fallback;
  }
  

  getProductName(product: ProductResponse, _lang?: string): string {
    return this.languageService.localizeMetaValue(product.meta_data, '', product.name);
  }

  getProductDisplayDescription(product: ProductResponse, _lang?: string): string {
    return this.languageService.localizeMetaValue(product.meta_data, 'description', product.description);
  }

  getCategoryDisplayName(category: CategoryResponse, _lang?: string): string {
    return this.languageService.localizeJsonMap(category.description, category.name || 'Uncategorized');
  }

  // Header flag chip: shown only on multilingual venues; reopens the language sheet in Switch mode.
  shouldShowSwitcher(): boolean {
    return this.languageService.shouldShowSwitcher();
  }

  // The active language's flag emoji (empty until the backend supplies flag_value — the chip then
  // falls back to the uppercased code in the template).
  get activeLanguageFlag(): string {
    const match = this.availableLanguages.find(l => l.code === this.currentLang);
    return match && match.flag_value && match.flag_value.trim() ? match.flag_value : '';
  }
  
  //  getProductDisplayAllergens(product: ProductResponse, metaDataKey: string): string {
  //   const meta = product.meta_data.find((m: { key: string; }) => m.key === `${metaDataKey}_short_description`);
  //   return meta?.value || product.short_description;
  // }


    hasValidImage(product: ProductResponse): boolean {
    return !!(product.images && product.images.length > 0 && product.images[0] && product.images[0].src);
  }

  getProductImageSrc(product: ProductResponse): string {
    if (this.hasValidImage(product)) {
      return product.images[0].src;
    }
    return '../../assets/ImageBroken.svg';
  }

  private preloadHeroImage(): void {
    // SVG <image> (load) is unreliable across browsers, so probe the hero URL with a
    // real Image() to drive the blinking-shadow placeholder on the menu header.
    const url = this.clientData?.ios_header_url || '../../assets/default.jpg';
    const img = new Image();
    img.onload = () => this.ngZone.run(() => { this.heroImageLoaded = true; this.cdr.markForCheck(); });
    img.onerror = () => this.ngZone.run(() => { this.heroImageLoaded = true; this.cdr.markForCheck(); });
    img.src = url;
  }

  onBannerImageLoad(): void { this.bannerImageLoaded = true; }
  onBannerImageError(): void { this.bannerImageLoaded = true; }

  getCatalogueIconSrc(): string {
    if (this.catalogueIconError || !this.clientData?.catalogue_icon) {
      return '../../assets/logo.png';
    }
    return this.clientData.catalogue_icon;
  }

  onCatalogueIconError(): void {
    this.catalogueIconError = true;
  }

  private async refreshClientData(): Promise<boolean> {
    if (!this.clientData?.id) return false;

    try {
      const freshClientData = await this.apiService.getClientData(this.clientData.id).toPromise();
      if (freshClientData) {
        // Update clientData with fresh data (including all feature flags)
        this.clientData = { ...this.clientData, ...freshClientData };
        // Update sessionStorage with fresh clientData
        sessionStorage.setItem('clientData', JSON.stringify(this.clientData));
        // Re-derive currency in case the shop's currency changed on refresh.
        this.currencyService.loadCurrency(this.clientData);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  private updateCallActionsStatus(): void {
    // Update call actions based on current clientData feature flags
    const isDineIn = this.orderingType === 'dine-in';
    const callActionsEnabled = this.clientData?.feature_flags?.ft_call_actions === true && this.clientData?.are_call_actions_active === true && this.clientData?.is_open_now === true;
    this.areCallActionsActive = isDineIn && callActionsEnabled;
  }

  private refreshLanguages(): void {
    if (!this.clientData?.id) return;

    try {
      // Update language-specific logic only (no API call - data already fetched)
      if (this.clientData.languages) {
        this.languageService.initializeForClient(this.clientData.id, this.clientData.languages);
      }
    } catch {
      // Keep existing value on error
    }
  }

  private trackCategoryViewed(categoryId: number): void {
    if (this.lastTrackedCategoryId === categoryId) {
      return;
    }

    const categoryGroup = this.categorizedProducts.find(cg => cg.category.id === categoryId);

    if (categoryGroup) {
      this.lastTrackedCategoryId = categoryId;

      this.analytics.log('menu_category_viewed', {
        category_id: categoryId.toString(),
        category_name: this.getCategoryDisplayName(categoryGroup.category, this.currentLang)
      });
    }
  }

  private trackCategorySelected(categoryId: number): void {
    const categoryGroup = this.categorizedProducts.find(cg => cg.category.id === categoryId);

    if (categoryGroup) {
      this.analytics.log('menu_category_selected', {
        category_id: categoryId.toString(),
        category_name: this.getCategoryDisplayName(categoryGroup.category, this.currentLang)
      });
    }
  }

  private trackSearchPerformed(searchString: string): void {
    // Calculate results count by performing the same search logic
    const resultsCount = this.products.filter(product =>
      product.name.toLowerCase().includes(searchString.toLowerCase()) ||
      product.description.toLowerCase().includes(searchString.toLowerCase())
    ).length;
    
    this.analytics.log('menu_searched', {
      search_string: searchString,
      results_count: resultsCount
    });
  }

  private trackSearchResultClicked(productId: number, productName: string, searchString: string): void {
    this.analytics.log('menu_search_result_clicked', {
      product_id: productId.toString(),
      product_name: productName,
      search_string: searchString
    });
  }

  private trackPreviousScreen(url: string): void {
    // Map routes to previous screen values
    if (url.includes('/product-details/')) {
      this.previousScreen = 'product_details';
    } else if (url.includes('/order-details')) {
      this.previousScreen = 'order';
    } else if (url.includes('/events')) {
      this.previousScreen = 'events_and_deals';
    } else if (url.includes('/menu')) {
      // If navigating to menu, don't change previous screen
      return;
    } else {
      this.previousScreen = 'default';
    }
  }

  private updatePreviousScreenForSearch(): void {
    // Update previous screen to 'search' when user enters search mode
    if (this.isSearchActive) {
      this.previousScreen = 'search';
    }
  }

  private trackMenuViewed(): void {
    if (!this.menuViewedTracked) {
      this.menuViewedTracked = true;
      
      this.analytics.log('menu_viewed', {
        previous_screen: this.previousScreen
      });
    }
  }

  private setupVisibilityDetection(): void {
    if (!this.menuContainer) return;
    
    // Check if Intersection Observer is supported
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Menu is visible, track the event
            this.trackMenuViewed();
          } else {
            // Menu is not visible, reset tracking for next time
            this.menuViewedTracked = false;
          }
        });
      }, {
        threshold: 0.1 // Trigger when 10% of the menu is visible
      });
      
      observer.observe(this.menuContainer.nativeElement);
    }
    
    // Also track immediately if menu is already visible (fallback and initial check)
    setTimeout(() => {
      if (this.menuContainer && this.menuContainer.nativeElement.getBoundingClientRect().top < window.innerHeight) {
        this.trackMenuViewed();
      }
    }, 100);
  }

  // ignoreOpenHours: answer "would ordering be on if the venue were open?" (used by the closed card).
  canEnableOrdering(ignoreOpenHours = false): boolean {
    const orderingType = sessionStorage.getItem('orderingType');
    
    // Rule 1: Table QR (dine-in)
    if (orderingType === 'dine-in') {
      return this.clientData?.feature_flags?.ft_orders_from_table === true && 
             this.isOrderingSupportedByExistingLogic(ignoreOpenHours);
    }
    
    // Rule 2: Main QR or Order Location (off-site/delivery)
    if (orderingType === 'off-site' || orderingType === 'delivery') {
      return this.clientData?.feature_flags?.ft_orders_from_home === true && 
             this.isOrderingSupportedByExistingLogic(ignoreOpenHours);
    }
    
    return false;
  }

  // Closed card: only for venues that take orders, and only when explicitly closed (missing is_open_now = open).
  // View-only menus never show it.
  isVenueClosedCardVisible(): boolean {
    return this.clientData?.is_open_now === false && this.canEnableOrdering(true);
  }

  private isOrderingSupportedByExistingLogic(ignoreOpenHours = false): boolean {
    // Match iOS isOrderingEnabled exactly: is_ordering_active must be true (nil/false → false), is_open_now/is_order_supported nil = OK.
    return this.clientData?.is_ordering_active === true &&
           (ignoreOpenHours || this.clientData?.is_open_now !== false) &&
           (this.clientData?.is_order_supported !== false);
  }

}

